import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import Location from '@solvhealth/types/interfaces/Location';
import { apiGetJson } from '.';
import { DAPI_HOST } from '../../config';
import { locationResponseFormatter } from '~/reducers/formatters/location';

/**
 * Fetches nearby premium in-person locations for a given lat/lng
 *
 * @returns a list of premium in-person locations
 */
export async function getNearbyPremiumLocations({
  lat,
  lng,
}: {
  lat: string | number | null;
  lng: string | number | null;
}) {
  if (!lat || !lng) {
    return null;
  }

  const queryParams = { lat: lat.toString(), lng: lng.toString() };

  const result = await apiGetJson<Location[]>(
    `${DAPI_HOST}/v1/premium-in-person/nearby?${new URLSearchParams(queryParams)}`
  );

  const locations = result?.data?.map?.(
    (location) => locationResponseFormatter(location) as Location
  );

  return locations;
}

/**
 * Hook to fetch nearby premium in-person locations for the user's
 * current position.
 *
 * @returns useQuery data
 */
export function useNearbyPremiumLocations() {
  const { userLocation, position } = useSelector((state) => ({
    userLocation: state.searchPreferences.location,
    position: state.position,
  }));

  let lat: string | number | null = null;
  let lng: string | number | null = null;

  if (userLocation?.latitude && userLocation.longitude) {
    lat = userLocation?.latitude;
    lng = userLocation?.longitude;
  } else if (position.result?.latitude && position.result?.longitude) {
    lat = position.result?.latitude;
    lng = position.result?.longitude;
  }

  return useQuery(
    ['nearby-locations', { lat, lng }],
    () => getNearbyPremiumLocations({ lat, lng }),
    {
      staleTime: 10,
    }
  );
}
